import React, { useState } from "react";
import { SettingsRow } from "../SettingsRow";
import { ArtistSubdomainModal, Button, useArtistContext } from "Components";
import { useArtistGroupSubdomain } from "hooks/useArtistGroupSubdomains";
import { H3 } from "melodies-source/Text";
import {
  useCollectionData,
  useDocumentData,
} from "react-firebase-hooks/firestore";
import {
  collection,
  doc,
  getFirestore,
  query,
  where,
} from "firebase/firestore";
import styled, { css } from "styled-components";
import { Link as RouterLink } from "react-router-dom";
import { Spinner } from "melodies-source/Spinner";
import { useUser } from "auth";
export const ArtistSubdomain: React.FC = () => {
  const { id: artistGroupId } = useArtistContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subdomain, subdomainLoading] = useArtistGroupSubdomain();
  const [surveys, surveysLoading] = useCollectionData(
    artistGroupId &&
      query(
        collection(getFirestore(), "sts3_surveys"),
        where("artistGroupId", "==", artistGroupId),
      ),
  );
  const [page, pageLoading] = useDocumentData(
    subdomain && doc(getFirestore(), "set_page", artistGroupId),
  );
  const { claims } = useUser();
  const loading = subdomainLoading || surveysLoading || pageLoading;
  const products = {
    "set.fan": {
      active:
        surveys?.filter((survey) => survey.status !== "deleted").length > 0,
      label: "Create a SET.Fan Campaign",
      path: `/${artistGroupId}/set-fan/surveys`,
    },
    "set.bio": {
      active: !!page,
      label: "Create Your First SET.Bio Page",
      path: `/${artistGroupId}/set-bio`,
    },
  };

  const toggleModalOpen = () => {
    setIsModalOpen((open) => !open);
  };

  return (
    <>
      <SettingsRow
        title={"Artist Subdomain"}
        description={
          <>
            Your artist subdomain links fans to featured SET.Fan campaigns and
            is used for your SET.Bio page. <br />
            {subdomain && (
              <LinkButton onClick={toggleModalOpen} text>
                {claims.admin ? "Update Subdomain" : "Request Change"}
              </LinkButton>
            )}
          </>
        }
      >
        {loading ? (
          <Spinner />
        ) : subdomain ? (
          <SubdomainContainer>
            <H3>{subdomain.id}</H3>
            <ProductsContainer>
              {Object.entries(products).map(([key, value]) => (
                <Product key={key} active={value.active}>
                  <span>
                    {subdomain.id}.{key}
                  </span>
                  {!value.active && <Link to={value.path}>{value.label}</Link>}
                </Product>
              ))}
            </ProductsContainer>
          </SubdomainContainer>
        ) : (
          <Button onClick={toggleModalOpen} text noPadding>
            Create Your Artist Subdomain
          </Button>
        )}
      </SettingsRow>
      <ArtistSubdomainModal
        isOpen={isModalOpen}
        onClose={toggleModalOpen}
        mode={subdomain ? "change" : "create"}
      />
    </>
  );
};

const SubdomainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${H3} {
    font-size: 22px;
  }

  ${({ theme }) => theme.media.desktop} {
    margin-top: 5px;
  }
`;
interface ProductProps {
  active: boolean;
}

const ProductsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 15px;

  ${({ theme }) => theme.media.mobile} {
    gap: 15px;
    margin-top: 5px;
    margin-bottom: 7px;
  }
`;

const Product = styled.div<ProductProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;

  ${({ active }) =>
    !active &&
    css`
      color: #999999;
      span {
        font-style: italic;
      }
    `};

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
`;
const linkStyle = css`
  font-weight: 600;
  color: var(--main-color);
  text-decoration: underline;
  font-size: 11px;
`;

const Link = styled(RouterLink)`
  ${linkStyle}
`;

const LinkButton = styled(Button)`
  padding: 0;
  height: auto;
  justify-content: flex-start;
  ${linkStyle}
`;
